import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Loader2, BookOpen } from "lucide-react";
import { getPricing, updatePricing, resetPricing, getReferenceQuotes, formatUSD } from "@/lib/api";

const Number2 = ({ value, onChange, step = 0.01, testid }) => (
  <input
    type="number"
    step={step}
    className="swiss-input"
    value={value}
    onChange={(e) => onChange(Number(e.target.value || 0))}
    data-testid={testid}
  />
);

const TableSection = ({ title, items, onItemChange, fieldKey = "unit_cost", fieldLabel = "Unit cost ($)", testidPrefix }) => (
  <div className="border border-[#050505] mb-px">
    <div className="border-b border-[#050505] px-5 py-4 bg-[#f5f5f5]">
      <h3 className="font-display font-bold text-lg uppercase tracking-tight">{title}</h3>
    </div>
    <table className="w-full">
      <thead>
        <tr className="border-b border-[#e5e5e5]">
          <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-[#525252]">Key</th>
          <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-[#525252]">Label</th>
          <th className="text-right px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-[#525252] w-48">
            {fieldLabel}
          </th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(items).map(([key, v]) => (
          <tr key={key} className="border-b border-[#e5e5e5] last:border-b-0">
            <td className="px-5 py-2 font-mono text-xs text-[#525252]">{key}</td>
            <td className="px-5 py-2 text-sm">{v.label}</td>
            <td className="px-5 py-2">
              <Number2
                value={v[fieldKey]}
                onChange={(val) => onItemChange(key, fieldKey, val)}
                step={fieldKey === "rate_per_page" ? 0.001 : 0.01}
                testid={`${testidPrefix}-${key}-${fieldKey}`}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Settings() {
  const [pricing, setPricing] = useState(null);
  const [refQuotes, setRefQuotes] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getPricing(), getReferenceQuotes()])
      .then(([p, r]) => {
        setPricing(p);
        setRefQuotes(r);
      })
      .catch(() => toast.error("Failed to load pricing"))
      .finally(() => setLoading(false));
  }, []);

  const updateGroup = (group, key, field, value) => {
    setPricing((p) => ({
      ...p,
      [group]: {
        ...p[group],
        [key]: { ...p[group][key], [field]: value },
      },
    }));
  };

  const updateTier = (idx, field, value) => {
    setPricing((p) => {
      const tiers = [...p.quantity_tiers];
      tiers[idx] = { ...tiers[idx], [field]: value };
      return { ...p, quantity_tiers: tiers };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updatePricing(pricing);
      setPricing(updated);
      toast.success("Pricing saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset pricing to defaults?")) return;
    try {
      const def = await resetPricing();
      setPricing(def);
      toast.success("Pricing reset to defaults");
    } catch {
      toast.error("Reset failed");
    }
  };

  if (loading || !pricing) {
    return (
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 lg:px-12 py-24 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-4" strokeWidth={1.5} />
        <div className="text-sm uppercase tracking-[0.2em] text-[#525252]">Loading…</div>
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 lg:px-12 py-12 lg:py-16" data-testid="settings-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
        <div>
          <div className="text-label mb-4">№ 004 — Control Room</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none">
            Pricing Settings
          </h1>
          <p className="mt-4 text-base text-[#525252] max-w-2xl">
            All rates in USD. Changes apply to all new quote calculations.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} className="btn-secondary" data-testid="reset-pricing-btn">
            <RotateCcw className="w-4 h-4" strokeWidth={1.5} /> Reset Defaults
          </button>
          <button onClick={save} className="btn-primary" disabled={saving} data-testid="save-pricing-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.5} />}
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </header>

      {/* Globals */}
      <div className="border border-[#050505] mb-px">
        <div className="border-b border-[#050505] px-5 py-4 bg-[#f5f5f5]">
          <h3 className="font-display font-bold text-lg uppercase tracking-tight">Global</h3>
        </div>

        {/* Markup slider — hero of the settings */}
        <div className="border-b border-[#e5e5e5] p-5 lg:p-6 bg-white" data-testid="markup-slider-section">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-label">Markup on Printer Cost</div>
              <div className="text-[11px] text-[#525252] uppercase tracking-[0.18em] mt-1">
                Applied globally · client price = printer cost × (1 + markup%)
              </div>
            </div>
            <div className="font-display font-black text-4xl tracking-tighter text-[#FF3333] font-mono" data-testid="markup-display">
              {Number(pricing.markup_pct || 0).toFixed(1)}%
            </div>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={0.5}
            value={pricing.markup_pct || 0}
            onChange={(e) => setPricing({ ...pricing, markup_pct: Number(e.target.value) })}
            className="w-full accent-[#FF3333]"
            data-testid="input-markup-pct"
          />
          <div className="flex justify-between mt-2 text-[10px] font-mono text-[#525252] uppercase tracking-[0.18em]">
            <span>0%</span><span>30%</span><span>50%</span><span>100%</span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#e5e5e5]">
          <div className="bg-white p-5">
            <label className="swiss-label">Setup fee ($)</label>
            <Number2 value={pricing.setup_fee} onChange={(v) => setPricing({ ...pricing, setup_fee: v })} testid="input-setup-fee" />
          </div>
          <div className="bg-white p-5">
            <label className="swiss-label">Proof fee ($)</label>
            <Number2 value={pricing.proof_fee || 0} onChange={(v) => setPricing({ ...pricing, proof_fee: v })} testid="input-proof-fee" />
          </div>
          <div className="bg-white p-5">
            <label className="swiss-label">Courier fee ($)</label>
            <Number2 value={pricing.courier_fee || 0} onChange={(v) => setPricing({ ...pricing, courier_fee: v })} testid="input-courier-fee" />
          </div>
          <div className="bg-white p-5">
            <label className="swiss-label">Tax rate (%)</label>
            <Number2 value={pricing.tax_rate} onChange={(v) => setPricing({ ...pricing, tax_rate: v })} testid="input-tax-rate" />
          </div>
        </div>
      </div>

      {/* Reference printer quotes */}
      {refQuotes && (
        <div className="border border-[#050505] mb-px" data-testid="reference-quotes-section">
          <div className="border-b border-[#050505] px-5 py-4 bg-[#050505] text-white flex items-center gap-3">
            <BookOpen className="w-4 h-4" strokeWidth={1.5} />
            <h3 className="font-display font-bold text-lg uppercase tracking-tight">Reference Quotes — South-African Printer</h3>
            <span className="ml-auto font-mono text-[11px] tracking-[0.18em] text-[#aaa]">
              FX used: R{refQuotes.fx_zar_per_usd} / $1
            </span>
          </div>
          <div className="px-5 py-3 text-[11px] text-[#525252] uppercase tracking-[0.18em] bg-[#f5f5f5] border-b border-[#e5e5e5]">
            These are the calibration anchors for the default printer rates. Adjust paper/cover/binding rates above until your calculator output matches the per-book USD cost shown here.
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#050505]">
                {["Quote", "Trim", "Pages", "Paper", "Binding", "Qty", "Total (ZAR)", "Per book (USD)"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.2em] text-[#525252] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {refQuotes.quotes.map((q) => (
                <tr key={q.id} className="border-b border-[#e5e5e5] last:border-b-0 hover:bg-[#f5f5f5]" data-testid={`ref-quote-${q.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs text-[#525252]">{q.id}</div>
                    <div className="text-sm">{q.title}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{q.trim}</td>
                  <td className="px-4 py-3 font-mono text-xs">{q.pages}pp</td>
                  <td className="px-4 py-3 font-mono text-xs">{q.paper}</td>
                  <td className="px-4 py-3 font-mono text-xs">{q.binding}</td>
                  <td className="px-4 py-3 font-mono text-xs">{q.quantity}</td>
                  <td className="px-4 py-3 font-mono text-xs">R{q.printer_total_native.toLocaleString()}</td>
                  <td className="px-4 py-3 font-mono text-sm font-bold text-[#FF3333]">{formatUSD(q.per_book_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TableSection
        title="Trim sizes (multiplier)"
        items={pricing.trim_sizes}
        fieldKey="multiplier"
        fieldLabel="Multiplier"
        onItemChange={(k, f, v) => updateGroup("trim_sizes", k, f, v)}
        testidPrefix="trim"
      />
      <TableSection
        title="Paper types ($ / page)"
        items={pricing.paper_types}
        fieldKey="rate_per_page"
        fieldLabel="Rate per page ($)"
        onItemChange={(k, f, v) => updateGroup("paper_types", k, f, v)}
        testidPrefix="paper"
      />
      <TableSection
        title="Color modes (multiplier)"
        items={pricing.color_modes}
        fieldKey="multiplier"
        fieldLabel="Multiplier"
        onItemChange={(k, f, v) => updateGroup("color_modes", k, f, v)}
        testidPrefix="color"
      />
      <TableSection
        title="Cover types ($ / copy)"
        items={pricing.cover_types}
        onItemChange={(k, f, v) => updateGroup("cover_types", k, f, v)}
        testidPrefix="cover"
      />
      <TableSection
        title="Binding ($ / copy)"
        items={pricing.binding_types}
        onItemChange={(k, f, v) => updateGroup("binding_types", k, f, v)}
        testidPrefix="binding"
      />
      <TableSection
        title="Finishing ($ / copy)"
        items={pricing.finishing_types}
        onItemChange={(k, f, v) => updateGroup("finishing_types", k, f, v)}
        testidPrefix="finishing"
      />

      {/* Quantity tiers */}
      <div className="border border-[#050505] mb-px">
        <div className="border-b border-[#050505] px-5 py-4 bg-[#f5f5f5]">
          <h3 className="font-display font-bold text-lg uppercase tracking-tight">Volume Discounts</h3>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#e5e5e5]">
              <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-[#525252]">Tier</th>
              <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-[#525252] w-40">Min qty</th>
              <th className="text-left px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-[#525252] w-40">Discount %</th>
            </tr>
          </thead>
          <tbody>
            {pricing.quantity_tiers.map((t, i) => (
              <tr key={i} className="border-b border-[#e5e5e5] last:border-b-0">
                <td className="px-5 py-2 font-mono text-xs text-[#525252]">Tier {i + 1}</td>
                <td className="px-5 py-2">
                  <Number2 value={t.min_qty} step={1} onChange={(v) => updateTier(i, "min_qty", v)} testid={`tier-min-${i}`} />
                </td>
                <td className="px-5 py-2">
                  <Number2 value={t.discount_pct} step={0.5} onChange={(v) => updateTier(i, "discount_pct", v)} testid={`tier-pct-${i}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
